/**
 * Dynamic Tool Loader Component
 * Robust bundle loading with timeout, retry, and error tracking
 *
 * This component:
 * 1. Loads tool bundles dynamically from R2 via the API route
 * 2. Implements timeout (5s) and retry logic (3 attempts with exponential backoff)
 * 3. Tracks errors for monitoring
 * 4. Creates an isolated mounting point for the tool
 * 5. Handles loading states and errors gracefully
 * 6. Cleans up on unmount
 *
 * Usage:
 *   <DynamicToolLoader slug="jwt-decoder" />
 *   <DynamicToolLoader slug="jwt-decoder" timeout={15000} maxRetries={5} />
 */

'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import Link from 'next/link';
import type { ToolBundle } from '@/lib/tool-bundle-wrapper';

// Error types for monitoring
export type BundleErrorCode =
  | 'TIMEOUT'
  | 'NETWORK_ERROR'
  | 'BUNDLE_NOT_FOUND'
  | 'CDN_ERROR'
  | 'RENDER_ERROR'
  | 'SCRIPT_ERROR'
  | 'OFFLINE'
  | 'MISSING_COMPONENT'
  | 'UNKNOWN';

export interface BundleLoadError {
  code: BundleErrorCode;
  message: string;
  slug: string;
  attempt: number;
  timestamp: number;
  details?: Record<string, unknown>;
}

// Global error tracking hook - can be overridden for monitoring integration
let globalErrorHandler: ((error: BundleLoadError) => void) | null = null;

export function setGlobalBundleErrorHandler(
  handler: (error: BundleLoadError) => void
) {
  globalErrorHandler = handler;
}

interface DynamicToolLoaderProps {
  slug: string;
  bundleUrl?: string;
  /** Bundle version from D1 metadata. Appended as ?v= query param to bust CDN cache on redeploys. */
  version?: number;
  timeout?: number; // Default: 5000ms (5 seconds)
  maxRetries?: number; // Default: 3
  retryDelay?: number; // Base delay for exponential backoff, default: 1000ms
  onLoad?: (bundleVersion: string) => void;
  onError?: (error: BundleLoadError) => void;
  /** SEO content rendered immediately for search engines. Should describe tool functionality. */
  seoContent?: React.ReactNode;
}

// Constants
/**
 * Cache epoch for unversioned bundle requests (embeds that can't resolve the
 * tool's bundle_version from D1, e.g. inline content embeds and force-static
 * KB pages). Unversioned URLs were historically edge-cached as immutable for
 * 1 year, so old bundles can be pinned at the edge under the bare URL; the
 * epoch param gives those embeds a fresh cache key. Bump this when stale
 * unversioned bundle responses need to be invalidated globally. The bundle
 * API caps unversioned responses at a 1-day edge TTL going forward.
 */
const BUNDLE_CACHE_EPOCH = 2;
const DEFAULT_TIMEOUT = 5000; // 5 seconds
const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_RETRY_DELAY = 1000; // 1 second base delay
const GLOBAL_POLL_INTERVAL = 5; // ms between polls for tool global
const GLOBAL_POLL_MAX_WAIT = 200; // ms max wait for tool global after script load
const SLOW_LOADING_THRESHOLD = 3000; // Show "taking longer" message after 3 seconds

// CDN URLs for heavy libraries (loaded on-demand)
const CDN_LIBRARIES = {
  'prop-types': 'https://cdn.jsdelivr.net/npm/prop-types@15.8.1/prop-types.min.js',
  recharts: 'https://cdn.jsdelivr.net/npm/recharts@2.15.0/umd/Recharts.min.js',
  jspdf: 'https://cdn.jsdelivr.net/npm/jspdf@3.0.4/dist/jspdf.umd.min.js',
  'jspdf-autotable': 'https://cdn.jsdelivr.net/npm/jspdf-autotable@5.0.2/dist/jspdf.plugin.autotable.min.js',
} as const;

// Global name mappings for CDN libraries
const CDN_GLOBAL_NAMES: Record<string, string> = {
  'prop-types': 'PropTypes',
  recharts: 'Recharts',
  jspdf: 'jspdf',
  'jspdf-autotable': 'autoTable',
};

// Tools that require recharts (preloaded from CDN)
// Updated 2026-02-01: Removed tools that no longer use recharts
const TOOLS_NEEDING_RECHARTS = new Set([
  'backup-recovery-time-calculator',
  'biometric-performance-simulator',
  'cve-lookup',
  'dr-site-cost-analyzer',
  'entropy-analyzer',
  'federated-identity-architect',
  'firewall-rule-simulator',
  'gdpr-role-retention-mapper',
  'microsoft-compliance-mapper',
  'microsoft-security-quote-builder',
  'mtbf-mttr-calculator',
  'pentest-scoping-calculator',
  'physical-security-planner',
  'quantitative-risk-analysis',
  'security-model-decision-matrix',
  'sla-slo-calculator',
  'substitution-cipher',
  'supply-chain-risk-assessor',
  'tcp-window-size-calculator',
  'threat-modeling-wizard',
  'vigenere-cipher',
  'wireless-security-planner',
  'data-classification-architect',
]);

// Tools that require jsPDF (preloaded from CDN)
// Updated 2026-02-01: Added missing tools that use jsPDF
const TOOLS_NEEDING_JSPDF = new Set([
  'backup-recovery-time-calculator',
  'biometric-performance-simulator',
  'business-impact-calculator',
  'cloud-security-self-assessment',
  'compliance-checklist',
  'cybersecurity-budget-calculator',
  'cybersecurity-maturity-assessment',
  'data-classification-architect',
  'domain-spoofing-detector',
  'dr-site-cost-analyzer',
  'federated-identity-architect',
  'firewall-rule-simulator',
  'gdpr-role-retention-mapper',
  'image-format-converter',
  'image-to-pdf-converter',
  'incident-response-playbook-generator',
  'interactive-ransomware-resilience-assessment',
  'media-sanitization-advisor',
  'microsoft-compliance-mapper',
  'microsoft-security-quote-builder',
  'pentest-scoping-calculator',
  'physical-security-planner',
  'quantitative-risk-analysis',
  'security-model-decision-matrix',
  'security-policy-generator',
  'supply-chain-risk-assessor',
  'threat-modeling-wizard',
  'wireless-security-planner',
]);

export default function DynamicToolLoader({
  slug,
  bundleUrl,
  version,
  timeout = DEFAULT_TIMEOUT,
  maxRetries = DEFAULT_MAX_RETRIES,
  retryDelay = DEFAULT_RETRY_DELAY,
  onLoad,
  onError,
  seoContent,
}: DynamicToolLoaderProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<BundleLoadError | null>(null);
  const [bundleVersion, setBundleVersion] = useState<string | null>(null);
  const [fallbackUsed, setFallbackUsed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isSlowLoading, setIsSlowLoading] = useState(false);

  // Report error to backend API (fire-and-forget)
  const reportErrorToApi = useCallback((err: BundleLoadError, isLastAttempt: boolean) => {
    // Only report to API on last attempt to avoid flooding
    if (!isLastAttempt) return;

    try {
      // Use sendBeacon for reliability (doesn't block, works during unload)
      const payload = JSON.stringify({
        action: 'report_error',
        slug: err.slug,
        error_code: err.code,
        error_message: err.message,
        load_attempt: err.attempt,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      });

      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon('/api/bundles/manage', payload);
      } else {
        // Fallback to fetch
        fetch('/api/bundles/manage', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
          keepalive: true,
        }).catch(() => {
          // Ignore reporting errors
        });
      }
    } catch {
      // Ignore reporting errors - non-critical
    }
  }, []);

  // Track error for monitoring
  const trackError = useCallback((err: BundleLoadError, isLastAttempt: boolean = false) => {
    console.error('[DynamicToolLoader] Error:', err);

    // Report to backend API for monitoring
    reportErrorToApi(err, isLastAttempt);

    // Call global handler if set (for Sentry, etc.)
    if (globalErrorHandler) {
      try {
        globalErrorHandler(err);
      } catch (handlerError) {
        console.error('[DynamicToolLoader] Error handler failed:', handlerError);
      }
    }

    // Call component-level handler
    if (onError) {
      onError(err);
    }
  }, [onError, reportErrorToApi]);

  // Create error object helper
  const createError = useCallback((
    code: BundleErrorCode,
    message: string,
    attempt: number,
    details?: Record<string, unknown>
  ): BundleLoadError => ({
    code,
    message,
    slug,
    attempt,
    timestamp: Date.now(),
    details,
  }), [slug]);

  // Load script with timeout
  const loadScriptWithTimeout = useCallback((
    url: string,
    timeoutMs: number
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const scriptElement = document.createElement('script');
      scriptElement.setAttribute('data-cfasync', 'false');
      scriptElement.src = url;
      scriptElement.async = true;
      scriptElement.crossOrigin = 'anonymous';

      // Set up timeout
      const timeoutId = setTimeout(() => {
        scriptElement.remove();
        reject(new Error(`Script load timeout after ${timeoutMs}ms`));
      }, timeoutMs);

      scriptElement.onload = () => {
        clearTimeout(timeoutId);
        // IIFE bundles execute synchronously before onload fires,
        // so the global is already set - resolve immediately
        resolve();
      };

      scriptElement.onerror = () => {
        clearTimeout(timeoutId);
        scriptElement.remove();
        reject(new Error('Script failed to load'));
      };

      document.head.appendChild(scriptElement);
    });
  }, []);

  // Load a CDN library script with timeout and error details
  const loadCDNLibrary = useCallback(async (name: keyof typeof CDN_LIBRARIES): Promise<unknown> => {
    const url = CDN_LIBRARIES[name];
    const globalName = CDN_GLOBAL_NAMES[name];

    // Check if already loaded
    if ((window as unknown as Record<string, unknown>)[globalName]) {
      return (window as unknown as Record<string, unknown>)[globalName];
    }

    // Check if script is already being loaded (avoid duplicates)
    const existingScript = document.querySelector(`script[src="${url}"]`);
    if (existingScript) {
      // Wait for existing script to finish loading
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          const lib = (window as unknown as Record<string, unknown>)[globalName];
          if (lib) {
            clearInterval(checkInterval);
            resolve(lib);
          }
        }, 100);
        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error(`Timeout waiting for ${name} to load (existing script)`));
        }, 10000);
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.setAttribute('data-cfasync', 'false');
      script.src = url;
      script.async = true;
      script.crossOrigin = 'anonymous';

      // Add timeout for CDN loading (15 seconds max)
      const cdnTimeout = setTimeout(() => {
        script.remove();
        reject(new Error(`CDN timeout: ${name} took too long to load from ${url}`));
      }, 15000);

      script.onload = () => {
        clearTimeout(cdnTimeout);
        // Wait for script execution, then poll for global with retries
        const checkGlobal = (retries: number) => {
          const lib = (window as unknown as Record<string, unknown>)[globalName];
          if (lib) {
            resolve(lib);
          } else if (retries > 0) {
            // Retry after a short delay - UMD scripts need time to execute
            setTimeout(() => checkGlobal(retries - 1), 100);
          } else {
            reject(new Error(`${globalName} not found after loading from ${url}`));
          }
        };
        // Check immediately - UMD scripts execute synchronously before onload
        checkGlobal(5);
      };

      script.onerror = (event) => {
        clearTimeout(cdnTimeout);
        script.remove();
        // Provide more detailed error message
        const errorMsg = navigator.onLine === false
          ? `Failed to load ${name}: No internet connection`
          : `Failed to load ${name} from CDN (${url}). The CDN may be blocked or unavailable.`;
        reject(new Error(errorMsg));
      };

      document.head.appendChild(script);
    });
  }, []);

  // Setup React globals (only once)
  const setupReactGlobals = useCallback(async () => {
    interface WindowWithReact extends Window {
      __REACT_LOADED__?: boolean;
      React?: typeof import('react');
      ReactDOM?: typeof import('react-dom');  // Use legacy react-dom for UMD compatibility
      require?: (moduleName: string) => unknown;
      __CDN_MODULES__?: Record<string, unknown>;
    }

    const win = window as unknown as WindowWithReact;

    if (win.__REACT_LOADED__) {
      return;
    }

    const React = await import('react');
    const ReactDOM = await import('react-dom/client');
    const ReactJSXRuntime = await import('react/jsx-runtime');
    const ReactDOMLegacy = await import('react-dom');

    // Create stub implementations for Next.js modules
    const NextLink = React.forwardRef<
      HTMLAnchorElement,
      { href: string; children: React.ReactNode; className?: string }
    >(({ href, children, className, ...props }, ref) => {
      return React.createElement('a', { href, className, ref, ...props }, children);
    });
    NextLink.displayName = 'Link';

    const NextNavigation = {
      useRouter: () => ({
        push: (url: string) => { window.location.href = url; },
        // CRITICAL: Preserve existing history.state to avoid corrupting Next.js internals
        // Next.js App Router maintains state with __NA object containing arrays (using unshift).
        // If we replace state with null, Next.js code crashes with "Cannot read properties of undefined (reading 'unshift')"
        replace: (url: string) => {
          const existingState = window.history.state;
          const newState = existingState ? { ...existingState } : {};
          window.history.replaceState(newState, '', url);
        },
        back: () => { window.history.back(); },
        forward: () => { window.history.forward(); },
        refresh: () => { window.location.reload(); },
        prefetch: () => { /* noop */ },
      }),
      usePathname: () => window.location.pathname,
      useSearchParams: () => new URLSearchParams(window.location.search),
    };

    // Initialize CDN module cache (will be populated lazily)
    win.__CDN_MODULES__ = {};

    const moduleCache: Record<string, unknown> = {
      'react': React,
      'react-dom': ReactDOMLegacy,
      'react-dom/client': ReactDOM,
      'react/jsx-runtime': ReactJSXRuntime,
      'next/link': { default: NextLink },
      'next/navigation': NextNavigation,
      'next/router': { useRouter: NextNavigation.useRouter },
      'crypto': {},
    };

    // Create a require function that handles CDN modules lazily
    win.require = (moduleName: string) => {
      // Check local module cache first
      if (moduleCache[moduleName]) {
        return moduleCache[moduleName];
      }

      // Check for CDN modules that were pre-loaded
      if (win.__CDN_MODULES__ && win.__CDN_MODULES__[moduleName]) {
        return win.__CDN_MODULES__[moduleName];
      }

      // Check if it's a CDN library by global name
      const globalName = CDN_GLOBAL_NAMES[moduleName];
      if (globalName) {
        const lib = (window as unknown as Record<string, unknown>)[globalName];
        if (lib) {
          // Cache for future use
          if (win.__CDN_MODULES__) {
            win.__CDN_MODULES__[moduleName] = lib;
          }
          return lib;
        }
        // Library not loaded yet - this will cause an error, but tools should handle it
        console.warn(`CDN library ${moduleName} not yet loaded. Consider preloading it.`);
      }

      throw new Error(`Module not found: ${moduleName}`);
    };

    win.React = React;
    // Set ReactDOM to legacy react-dom for UMD library compatibility (recharts needs render method)
    win.ReactDOM = ReactDOMLegacy;
    win.__REACT_LOADED__ = true;
  }, []);

  // Main loading effect
  useEffect(() => {
    let mounted = true;
    abortControllerRef.current = new AbortController();

    async function attemptLoad(attempt: number): Promise<boolean> {
      if (!mounted) return false;

      const url = bundleUrl || `/api/tools/${slug}/bundle.js${version ? `?v=${version}` : `?e=${BUNDLE_CACHE_EPOCH}`}`;

      console.log(`[DynamicToolLoader] Attempt ${attempt}/${maxRetries} for ${slug}`);

      try {
        // Check for offline status before attempting load
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          throw new Error('No internet connection');
        }

        // Setup React globals
        await setupReactGlobals();

        // Preload CDN libraries that this tool needs
        // Use Promise.allSettled to handle partial CDN failures gracefully
        if (TOOLS_NEEDING_RECHARTS.has(slug)) {
          console.log(`[DynamicToolLoader] Preloading recharts dependencies for ${slug}`);
          // Recharts requires React, ReactDOM, and PropTypes as globals
          // React/ReactDOM are set in setupReactGlobals, PropTypes needs CDN loading
          await loadCDNLibrary('prop-types');
          console.log(`[DynamicToolLoader] Preloading recharts for ${slug}`);
          await loadCDNLibrary('recharts');
        }
        if (TOOLS_NEEDING_JSPDF.has(slug)) {
          console.log(`[DynamicToolLoader] Preloading jsPDF for ${slug}`);
          await loadCDNLibrary('jspdf');
          await loadCDNLibrary('jspdf-autotable');
        }

        // Load script with timeout
        await loadScriptWithTimeout(url, timeout);

        if (!mounted) return false;

        // Get tool bundle from global scope
        // IIFE bundles set the global synchronously before onload, so it's
        // usually available immediately. Poll briefly as a safety net.
        const globalName = `Tool_${slug.replace(/-/g, '_')}`;
        let toolBundleRaw = (window as unknown as Record<string, unknown>)[globalName] as
          ToolBundle | { default: ToolBundle } | undefined;

        if (!toolBundleRaw) {
          const pollStart = Date.now();
          while (!toolBundleRaw && Date.now() - pollStart < GLOBAL_POLL_MAX_WAIT) {
            await new Promise(r => setTimeout(r, GLOBAL_POLL_INTERVAL));
            toolBundleRaw = (window as unknown as Record<string, unknown>)[globalName] as
              ToolBundle | { default: ToolBundle } | undefined;
          }
        }

        if (!toolBundleRaw) {
          throw createError(
            'BUNDLE_NOT_FOUND',
            `Tool bundle global "${globalName}" not found after script load`,
            attempt,
            { globalName, url }
          );
        }

        // Handle ES module default export
        const toolBundle: ToolBundle =
          'default' in toolBundleRaw && typeof toolBundleRaw.default === 'object'
            ? (toolBundleRaw.default as ToolBundle)
            : (toolBundleRaw as ToolBundle);

        // Render tool in mount point
        if (mounted && mountRef.current) {
          const React = await import('react');
          const ReactDOM = await import('react-dom/client');

          const Component = toolBundle.Component;
          // A bundle that loads successfully but doesn't expose .Component
          // (older R2 artifact, build regression, partial chunk) would
          // hit React.createElement(undefined) and throw React #130. Treat
          // it like a load failure so the retry / error UI takes over.
          if (typeof Component !== 'function') {
            throw createError(
              'MISSING_COMPONENT',
              `Bundle for "${slug}" did not export a Component`,
              attempt,
            );
          }

          const root = ReactDOM.createRoot(mountRef.current);
          root.render(React.createElement(Component));

          cleanupRef.current = () => root.unmount();
        }

        // Fetch version info in background
        fetch(url, { method: 'HEAD' })
          .then(response => {
            if (mounted) {
              const version = response.headers.get('X-Bundle-Version');
              const fallback = response.headers.get('X-Bundle-Fallback-Used') === 'true';
              setBundleVersion(version);
              setFallbackUsed(fallback);
              if (onLoad && version) onLoad(version);
            }
          })
          .catch(() => {
            // Ignore version fetch errors - non-critical
          });

        return true; // Success
      } catch (err) {
        if (!mounted) return false;

        // Determine error type with improved classification
        let bundleError: BundleLoadError;

        if (err instanceof Error) {
          const msg = err.message.toLowerCase();

          // Check for offline/connectivity issues first
          if (msg.includes('no internet') || navigator.onLine === false) {
            bundleError = createError('OFFLINE', 'No internet connection. Please check your network and try again.', attempt);
          }
          // CDN-specific errors
          else if (msg.includes('cdn') || msg.includes('jsdelivr') || msg.includes('recharts') || msg.includes('jspdf') || msg.includes('prop-types')) {
            bundleError = createError('CDN_ERROR', err.message, attempt, {
              cdn: 'jsdelivr',
              suggestion: 'Try refreshing the page or check if content blockers are enabled'
            });
          }
          // Timeout errors
          else if (msg.includes('timeout')) {
            bundleError = createError('TIMEOUT', err.message, attempt, { timeout });
          }
          // Bundle not found
          else if (msg.includes('not found')) {
            bundleError = createError('BUNDLE_NOT_FOUND', err.message, attempt);
          }
          // Script load failures
          else if (msg.includes('script failed') || msg.includes('failed to load')) {
            bundleError = createError('NETWORK_ERROR', err.message, attempt, { url });
          }
          // Unknown errors
          else {
            bundleError = createError('UNKNOWN', err.message, attempt);
          }
        } else if ((err as BundleLoadError).code) {
          bundleError = err as BundleLoadError;
        } else {
          bundleError = createError('UNKNOWN', 'Unknown error occurred', attempt);
        }

        // Check if this is the last attempt (used for API error reporting)
        const isLastAttempt = attempt >= maxRetries;

        // Track error (only reports to API on last attempt)
        trackError(bundleError, isLastAttempt);

        // Check if we should retry
        if (!isLastAttempt) {
          const delay = retryDelay * Math.pow(2, attempt - 1); // Exponential backoff
          console.log(`[DynamicToolLoader] Retrying in ${delay}ms...`);

          await new Promise(resolve => setTimeout(resolve, delay));

          if (mounted) {
            setRetryCount(attempt);
            return attemptLoad(attempt + 1);
          }
        }

        // All retries exhausted
        if (mounted) {
          setError(bundleError);
          setLoading(false);
        }

        return false;
      }
    }

    // Start loading
    setLoading(true);
    setError(null);
    setRetryCount(0);

    attemptLoad(1).then(success => {
      if (mounted && success) {
        setLoading(false);
      }
    });

    // Cleanup
    return () => {
      mounted = false;
      abortControllerRef.current?.abort();

      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [slug, bundleUrl, timeout, maxRetries, retryDelay, onLoad, setupReactGlobals, loadScriptWithTimeout, createError, trackError]);

  // Track slow loading - show message after 3 seconds
  useEffect(() => {
    if (!loading) {
      setIsSlowLoading(false);
      return;
    }

    const slowTimer = setTimeout(() => {
      setIsSlowLoading(true);
    }, SLOW_LOADING_THRESHOLD);

    return () => clearTimeout(slowTimer);
  }, [loading]);

  // Retry handler for manual retry
  const handleRetry = useCallback(() => {
    setError(null);
    setLoading(true);
    setRetryCount(0);
    // Force re-mount by changing key (handled by parent if needed)
    window.location.reload();
  }, []);

  // Stable bundle URL for preloading (must match the URL used in loadScriptWithTimeout)
  const preloadUrl = bundleUrl || `/api/tools/${slug}/bundle.js${version ? `?v=${version}` : `?e=${BUNDLE_CACHE_EPOCH}`}`;

  return (
    <div suppressHydrationWarning>
      {/* Preload hints - React 19 hoists these to <head> during SSR so the browser
          starts fetching before hydration and before useEffect fires */}
      <link rel="preload" href={preloadUrl} as="script" crossOrigin="anonymous" />
      {TOOLS_NEEDING_RECHARTS.has(slug) && (
        <>
          <link rel="preload" href={CDN_LIBRARIES['prop-types']} as="script" crossOrigin="anonymous" />
          <link rel="preload" href={CDN_LIBRARIES.recharts} as="script" crossOrigin="anonymous" />
        </>
      )}
      {TOOLS_NEEDING_JSPDF.has(slug) && (
        <>
          <link rel="preload" href={CDN_LIBRARIES.jspdf} as="script" crossOrigin="anonymous" />
          <link rel="preload" href={CDN_LIBRARIES['jspdf-autotable']} as="script" crossOrigin="anonymous" />
        </>
      )}

      {/* SEO Content - Always in DOM for search engines, hidden visually once tool loads */}
      {seoContent && (
        <div
          className={loading ? 'block' : 'sr-only'}
          aria-hidden={!loading}
          inert={!loading}
          suppressHydrationWarning
        >
          {seoContent}
        </div>
      )}

      {/* Loading state with skeleton UI - shown when no SEO content provided */}
      {loading && !seoContent && (
        <div className="space-y-6" suppressHydrationWarning>
          {/* Progress bar */}
          <div className="w-full h-1 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full w-full bg-gradient-to-r from-blue-500 to-blue-600 animate-tool-bundle-loading origin-left" />
          </div>

          {/* Loading status */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="h-5 w-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
            </div>
            <div className="text-sm">
              <p className="text-gray-600 font-medium">
                {retryCount > 0
                  ? `Loading tool... (retry ${retryCount}/${maxRetries})`
                  : TOOLS_NEEDING_RECHARTS.has(slug)
                    ? 'Loading tool & charts...'
                    : 'Loading tool...'}
              </p>
              {isSlowLoading && (
                <p className="text-amber-600 text-xs mt-1">
                  Taking longer than expected. Please wait...
                </p>
              )}
            </div>
          </div>

          {/* Skeleton tabs */}
          <div className="flex gap-1 border-b border-gray-200 animate-pulse">
            <div className="px-4 py-2 bg-blue-100 rounded-t-lg">
              <div className="h-4 w-16 bg-blue-200 rounded" />
            </div>
            <div className="px-4 py-2">
              <div className="h-4 w-20 bg-gray-200 rounded" />
            </div>
            <div className="px-4 py-2">
              <div className="h-4 w-14 bg-gray-200 rounded" />
            </div>
          </div>

          {/* Skeleton input area */}
          <div className="space-y-3 animate-pulse">
            <div className="h-4 w-28 bg-gray-200 rounded" />
            <div className="w-full h-32 border border-gray-200 rounded-lg bg-gray-50/80 p-4">
              <div className="space-y-3">
                <div className="h-3 bg-gray-200 rounded w-full" />
                <div className="h-3 bg-gray-200 rounded w-4/5" />
                <div className="h-3 bg-gray-200 rounded w-2/3" />
              </div>
            </div>
          </div>

          {/* Skeleton action buttons */}
          <div className="flex flex-wrap gap-3 animate-pulse">
            <div className="px-6 py-2.5 bg-blue-100 rounded-lg">
              <div className="h-4 w-16 bg-blue-200 rounded" />
            </div>
            <div className="px-6 py-2.5 bg-gray-100 rounded-lg border border-gray-200">
              <div className="h-4 w-12 bg-gray-200 rounded" />
            </div>
            <div className="px-6 py-2.5 bg-gray-100 rounded-lg border border-gray-200">
              <div className="h-4 w-10 bg-gray-200 rounded" />
            </div>
          </div>

          {/* Skeleton output area */}
          <div className="space-y-3 animate-pulse">
            <div className="h-4 w-20 bg-gray-200 rounded" />
            <div className="w-full h-28 border border-gray-200 rounded-lg bg-gray-50/80 p-4">
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
                <div className="h-3 bg-gray-200 rounded w-5/6" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading skeleton overlay when SEO content is shown */}
      {loading && seoContent && (
        <div className="space-y-4 py-4" suppressHydrationWarning>
          {/* Progress bar - slim and subtle */}
          <div className="w-full h-0.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full w-full bg-gradient-to-r from-blue-400 to-blue-600 animate-tool-bundle-loading" />
          </div>

          {/* Compact loading indicator */}
          <div className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="h-4 w-4 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
              <span className="text-sm text-blue-700 font-medium">
                {TOOLS_NEEDING_RECHARTS.has(slug)
                  ? 'Loading interactive tool & charts...'
                  : 'Loading interactive tool...'}
              </span>
            </div>
            {isSlowLoading && (
              <span className="text-amber-600 text-xs">Taking longer than expected...</span>
            )}
          </div>

          {/* Mini skeleton preview */}
          <div className="animate-pulse space-y-3 p-4 border border-gray-100 rounded-lg bg-gray-50/50">
            <div className="flex gap-2 border-b border-gray-100 pb-3">
              <div className="h-6 w-16 bg-blue-100 rounded" />
              <div className="h-6 w-20 bg-gray-200 rounded" />
              <div className="h-6 w-14 bg-gray-200 rounded" />
            </div>
            <div className="h-8 bg-gray-200 rounded w-full" />
            <div className="flex gap-2">
              <div className="h-8 w-20 bg-blue-100 rounded" />
              <div className="h-8 w-16 bg-gray-100 rounded border border-gray-200" />
            </div>
          </div>
        </div>
      )}

      {/* Error state with user-friendly messages */}
      {error && (
        <div className={`rounded-lg p-6 my-8 ${error.code === 'OFFLINE' ? 'bg-amber-50 border border-amber-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className={`h-6 w-6 ${error.code === 'OFFLINE' ? 'text-amber-600' : 'text-red-600'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {error.code === 'OFFLINE' ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                )}
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className={`text-sm font-medium ${error.code === 'OFFLINE' ? 'text-amber-800' : 'text-red-800'}`}>
                {error.code === 'OFFLINE' ? 'Connection Issue' :
                 error.code === 'CDN_ERROR' ? 'Resource Loading Error' :
                 error.code === 'TIMEOUT' ? 'Loading Timeout' :
                 'Error Loading Tool'}
              </h3>
              <p className={`mt-2 text-sm ${error.code === 'OFFLINE' ? 'text-amber-700' : 'text-red-700'}`}>
                {error.code === 'OFFLINE'
                  ? 'Please check your internet connection and try again.'
                  : error.code === 'CDN_ERROR'
                    ? 'Some resources failed to load. This might be caused by an ad blocker, firewall, or network restrictions.'
                    : error.code === 'TIMEOUT'
                      ? 'The tool is taking too long to load. This could be due to slow network conditions.'
                      : error.message}
              </p>
              {/* Troubleshooting tips */}
              {(error.code === 'CDN_ERROR' || error.code === 'NETWORK_ERROR') && (
                <div className="mt-3 text-xs text-gray-600 bg-gray-100 rounded p-2">
                  <p className="font-medium mb-1">Troubleshooting tips:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>Disable ad blockers or content blockers for this site</li>
                    <li>Check if your firewall allows connections to cdn.jsdelivr.net</li>
                    <li>Try using a different browser or network</li>
                  </ul>
                </div>
              )}
              <div className={`mt-2 text-xs ${error.code === 'OFFLINE' ? 'text-amber-500' : 'text-red-500'}`}>
                Error code: {error.code} | Attempts: {error.attempt}/{maxRetries}
              </div>
              <div className="mt-4 flex gap-3">
                <button
                  onClick={handleRetry}
                  className={`text-sm font-medium underline ${error.code === 'OFFLINE' ? 'text-amber-600 hover:text-amber-500' : 'text-red-600 hover:text-red-500'}`}
                >
                  Try again
                </button>
                <Link
                  href="/tools"
                  className="text-sm font-medium text-gray-600 hover:text-gray-500"
                >
                  Browse other tools
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fallback version warning */}
      {!loading && !error && fallbackUsed && bundleVersion && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-800">
                Using fallback version (v{bundleVersion}). The latest version may not be available yet.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tool mount point - ALWAYS rendered so ref is available */}
      <div ref={mountRef} style={{ display: loading || error ? 'none' : 'block' }} suppressHydrationWarning />

      {/* Noscript fallback for search engines and users without JavaScript */}
      <noscript>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 my-4">
          <h3 className="text-lg font-semibold text-amber-800 mb-2">JavaScript Required</h3>
          <p className="text-amber-700 mb-4">
            This interactive tool requires JavaScript to function. Please enable JavaScript in your browser to use the full features.
          </p>
          <p className="text-amber-600 text-sm">
            The tool description and documentation above provide information about this tool&apos;s capabilities.
            For the best experience, please enable JavaScript and refresh the page.
          </p>
        </div>
      </noscript>
    </div>
  );
}
